/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2020 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * React component for Generic Error Display
 *
 * @file ErrorCard.jsx
 * @author Infant Diana Sebastian
 */

import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { Typography, Box, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import Framework from '@greenville/framework';
import { EventProvider } from '@aquila/core';
import ErrorMessageIcon from '../icons/ErrorMessageIcon';
import { ERROR_SUPPORT, ERROR_SUPPORT_CLICK } from '../constants';

const Wrapper = styled('div')({
  '&&': {
    marginTop: 120
  }
});

const BoxWrapper = styled(Box)({
  '&&': {
    width: '34.7%',
    paddingLeft: '9%',
    paddingRight: '9%',
    height: 416,
    boxShadow: '0px 7px 8px rgba(0, 0, 0, 0.05), 0px 5px 22px rgba(0, 0, 0, 0.03)',
    borderRadius: 8,
    textAlign: 'center',
    margin: 'auto'
  }
});

const ErrorTitle = styled(Typography)({
  '&&': {
    marginBottom: 16,
    fontWeight: 600
  }
});

const ErrorImage = styled(ErrorMessageIcon)({
  '&&': {
    paddingTop: 50,
    width: '100%',
    '@media (max-width: 808px)': {
      height: 'auto',
      paddingTop: 20
    }
  }
});

const ErrorDesc = styled(Typography)({
  '&&': {
    marginBottom: 16
  }
});

const ErrorCode = styled(Typography)({
  '&&': {
    fontWeight: 600
  }
});

const ErrorCard = ({
  code, courseId, productId
}) => {
  const language = Framework.getStoreRegistry().getStore('language');

  const handleSupport = () => {
    const userId = Framework.getStoreRegistry().getStore('user').id;

    EventProvider.getEventManager().dispatch({
      event: ERROR_SUPPORT,
      category: ERROR_SUPPORT,
      action: ERROR_SUPPORT_CLICK,
      data: {
        courseId,
        productId,
        userId
      }
    });
  };

  return (
    <Wrapper>
      <BoxWrapper>
        <ErrorImage />
        <ErrorTitle variant="h2">
          <FormattedMessage {...language.getText('errors.GENERIC_TITLE')} />
        </ErrorTitle>
        <ErrorDesc variant="h5">
          <FormattedMessage {...language.getText('errors.GENERIC_DESC')} />
          <Link
            href={language.getMessage('link.SUPPORT')}
            color="inherit"
            target="_blank"
            rel="noopener"
            underline="always"
            onClick={handleSupport}
          >
            <Typography variant="h5" component="span">
              <FormattedMessage {...language.getText('errors.GENERIC_LINK')} />
            </Typography>
          </Link>
        </ErrorDesc>
        <ErrorCode variant="subtitle1">
          <FormattedMessage
            {...language.getText('errors.GENERIC_CODE')}
            values={
              {
                code
              }
            }
          />
        </ErrorCode>
      </BoxWrapper>
    </Wrapper>
  );
};

ErrorCard.propTypes = {
  code: PropTypes.string.isRequired,
  courseId: PropTypes.string,
  productId: PropTypes.string
};

ErrorCard.defaultProps = {
  courseId: '',
  productId: ''
};

export default ErrorCard;
